import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Plus, Trash2, Edit, X, Check, Loader2, Megaphone, Save,
  FileText, ListTree, Settings as SettingsIcon, Copy, RefreshCw,
  GripVertical, Shuffle, Lock, Unlock, Zap, Layers, ChevronDown, AlertCircle
} from 'lucide-react'
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { campaignApi, avatarApi, API_BASE_URL } from '../services/api'
import { useAuth } from '../context/AuthContext'
import './AvatarDetails.css'

const getLabelDisplay = (name) => (name || '').replace(/_/g, ' ')

const ACTION_TYPE_PILLS = [
  { value: 'regular', label: 'Regular' },
  { value: 'interrupt', label: 'Interrupt' },
  { value: 'live', label: 'Live' },
  { value: 'on_silence', label: 'On Silence' },
  { value: 'on_not_interested', label: 'On Not Interested' }
]
const DISPOSITION_EMITS = [
  'not-interested', 'do-not-call', 'age-do-not-qualify', 'answering-machine',
  'transfer-call', 'call-back', 'busy', 'no-answer', 'dead-air', 'no-pitch',
  'hangup', 'sale', 'back-one-step'
]

// Clone of AvatarDetails' SearchableSelect (reuses searchable-select* CSS).
function SearchableSelect({ options, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef(null)
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch('') } }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])
  const filtered = (options || []).filter(o => (o || '').toLowerCase().includes(search.toLowerCase()))
  return (
    <div className="searchable-select" ref={ref} onClick={e => e.stopPropagation()}>
      <div className={`searchable-select-trigger ${open ? 'open' : ''}`} onClick={() => setOpen(o => !o)}>
        <span className={`searchable-select-value ${!value || value === '- none -' ? 'placeholder' : ''}`}>
          {value && value !== '- none -' ? value : placeholder}
        </span>
        <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </div>
      {open && (
        <div className="searchable-select-dropdown">
          <div className="searchable-select-search">
            <input type="text" placeholder="Search..." value={search} autoFocus
              onChange={e => setSearch(e.target.value)} onClick={e => e.stopPropagation()} />
          </div>
          <div className="searchable-select-options">
            {filtered.length === 0 ? (
              <div className="searchable-select-option no-results">No matches found</div>
            ) : filtered.map((o, i) => (
              <div key={`${o}-${i}`} className={`searchable-select-option ${o === value ? 'selected' : ''}`}
                onClick={() => { onChange(o); setOpen(false); setSearch('') }}>{o}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Flow tokens an action can emit (terminal disposition, advance, or goto-step-N typed manually)
const EMIT_OPTIONS = [
  '', 'not-interested', 'do-not-call', 'age-do-not-qualify', 'answering-machine',
  'transfer-call', 'call-back', 'busy', 'no-answer', 'dead-air', 'no-pitch',
  'hangup', 'sale', 'back-one-step'
]
const ACTION_TYPES = ['regular', 'on_silence', 'live']

function blankAction() {
  return {
    step_number: 1, order_idx: 0, action_type: 'regular', array: '- no condition -',
    audio_category_1: '', audio_category_2: '', audio_category_3: '',
    emit: '', increase_step: 0, can_interrupt: 1
  }
}

export default function CampaignBuilder() {
  const { user } = useAuth()
  const [campaigns, setCampaigns] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState(null)
  const [detail, setDetail] = useState(null)   // { campaign, scripts, actions }
  const [detailLoading, setDetailLoading] = useState(false)
  const [tab, setTab] = useState('settings')

  // toast
  const [toasts, setToasts] = useState([])
  const tidRef = useRef(0)
  const toast = useCallback((message, type = 'info', ms = 3500) => {
    const id = ++tidRef.current
    setToasts(p => [...p, { id, message, type }])
    if (ms > 0) setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), ms)
  }, [])

  const audit = () => ({ user_id: user?.id || null, user_name: user?.username || user?.name || null })

  const loadCampaigns = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await campaignApi.getAll()
      setCampaigns(data.campaigns || [])
    } catch (e) { toast('Failed to load campaigns: ' + e.message, 'error') }
    finally { setLoading(false) }
  }, [toast])

  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return }
    setDetailLoading(true)
    try {
      const { data } = await campaignApi.getById(id)
      setDetail(data)
    } catch (e) { toast('Failed to load campaign: ' + e.message, 'error') }
    finally { setDetailLoading(false) }
  }, [toast])

  useEffect(() => { loadCampaigns() }, [loadCampaigns])
  useEffect(() => { loadDetail(selectedId) }, [selectedId, loadDetail])

  // ---- create (blank or seed from avatar) ----
  const [showCreate, setShowCreate] = useState(false)

  const onCreated = async (campaign) => {
    setShowCreate(false)
    await loadCampaigns()
    setSelectedId(campaign.id)
    setTab('settings')
    toast(`Campaign "${campaign.name}" created`, 'success')
  }

  const deleteCampaign = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"? Its scripts and avatar flow will be removed.`)) return
    try {
      await campaignApi.delete(c.id, true)
      if (selectedId === c.id) { setSelectedId(null); setDetail(null) }
      await loadCampaigns()
      toast('Campaign deleted', 'success')
    } catch (e) { toast('Delete failed: ' + e.message, 'error') }
  }

  return (
    <div style={{ padding: 24 }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Megaphone size={26} />
          <div>
            <h1 style={{ margin: 0, fontSize: 22 }}>Campaign Builder</h1>
            <div style={{ opacity: 0.6, fontSize: 13 }}>Reusable avatar blueprints — scripts + flow. Picked when creating a voice set.</div>
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <Plus size={16} /> New Campaign
        </button>
      </div>

      <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>
        {/* list */}
        <div style={{ width: 280, flexShrink: 0 }}>
          {loading ? (
            <div style={{ padding: 20, opacity: 0.6 }}><Loader2 className="spin" size={18} /> Loading…</div>
          ) : campaigns.length === 0 ? (
            <div style={{ padding: 20, opacity: 0.6, fontSize: 13 }}>No campaigns yet. Create one — or seed from an existing avatar.</div>
          ) : campaigns.map(c => (
            <div key={c.id}
              onClick={() => setSelectedId(c.id)}
              style={{
                padding: '12px 14px', marginBottom: 8, borderRadius: 10, cursor: 'pointer',
                border: '1px solid ' + (selectedId === c.id ? 'rgba(139,92,246,0.7)' : 'var(--border-color)'),
                background: selectedId === c.id ? 'rgba(139,92,246,0.12)' : 'var(--bg-card)'
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong>{c.name}</strong>
                <Trash2 size={15} style={{ opacity: 0.5 }} onClick={(e) => { e.stopPropagation(); deleteCampaign(c) }} />
              </div>
              <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
                {c.company || '—'} · {c.script_count} scripts · {c.action_count} actions
              </div>
            </div>
          ))}
        </div>

        {/* detail */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {!selectedId ? (
            <div style={{ padding: 40, textAlign: 'center', opacity: 0.5 }}>Select a campaign, or create one.</div>
          ) : detailLoading || !detail ? (
            <div style={{ padding: 40 }}><Loader2 className="spin" size={20} /> Loading…</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 6, marginBottom: 16, borderBottom: '1px solid var(--border-color)' }}>
                {[['settings', 'Settings', SettingsIcon], ['scripts', `Scripts (${detail.scripts.length})`, FileText], ['flow', `Avatar Flow (${detail.actions.length})`, ListTree]].map(([key, label, Icon]) => (
                  <button key={key} onClick={() => setTab(key)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: '10px 14px',
                      color: 'inherit', opacity: tab === key ? 1 : 0.55,
                      borderBottom: '2px solid ' + (tab === key ? 'rgba(139,92,246,0.9)' : 'transparent')
                    }}>
                    <Icon size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />{label}
                  </button>
                ))}
              </div>

              {tab === 'settings' && <SettingsTab detail={detail} audit={audit} toast={toast} onSaved={() => { loadDetail(selectedId); loadCampaigns() }} />}
              {tab === 'scripts' && <ScriptsTab detail={detail} toast={toast} reload={() => loadDetail(selectedId)} />}
              {tab === 'flow' && <FlowTab detail={detail} toast={toast} reload={() => loadDetail(selectedId)} />}
            </>
          )}
        </div>
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={onCreated} toast={toast} audit={audit} />}

      {/* toasts */}
      <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999 }}>
        {toasts.map(t => (
          <div key={t.id} style={{
            marginTop: 8, padding: '10px 16px', borderRadius: 8, minWidth: 240, color: '#fff',
            background: t.type === 'error' ? '#dc2626' : t.type === 'warning' ? '#d97706' : t.type === 'success' ? '#16a34a' : '#374151'
          }}>{t.message}</div>
        ))}
      </div>
    </div>
  )
}

// ============================================================ SETTINGS TAB
function SettingsTab({ detail, audit, toast, onSaved }) {
  const [form, setForm] = useState(detail.campaign)
  const [saving, setSaving] = useState(false)
  useEffect(() => { setForm(detail.campaign) }, [detail.campaign])
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const save = async () => {
    setSaving(true)
    try {
      await campaignApi.update(detail.campaign.id, { ...form, ...audit() })
      toast('Saved', 'success'); onSaved()
    } catch (e) { toast('Save failed: ' + e.message, 'error') }
    finally { setSaving(false) }
  }

  const F = ({ label, k, type = 'text', w }) => (
    <div style={{ marginBottom: 12, width: w || '100%' }}>
      <label style={{ display: 'block', fontSize: 12, opacity: 0.6, marginBottom: 4 }}>{label}</label>
      <input className="input" type={type} value={form[k] ?? ''} onChange={e => set(k, type === 'number' ? e.target.value : e.target.value)} />
    </div>
  )

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ display: 'flex', gap: 14 }}>
        <F label="Name" k="name" w="50%" />
        <F label="Company" k="company" w="50%" />
      </div>
      <F label="Description" k="description" />
      <div style={{ display: 'flex', gap: 14 }}>
        <div style={{ width: '50%' }}>
          <label style={{ display: 'block', fontSize: 12, opacity: 0.6, marginBottom: 4 }}>Voice mode</label>
          <select className="input" value={form.voice_mode || 'modern'} onChange={e => set('voice_mode', e.target.value)}>
            <option value="modern">modern</option><option value="classic">classic</option>
          </select>
        </div>
        <div style={{ width: '50%' }}>
          <label style={{ display: 'block', fontSize: 12, opacity: 0.6, marginBottom: 4 }}>Keyword mode</label>
          <select className="input" value={form.keyword_mode || 'modern'} onChange={e => set('keyword_mode', e.target.value)}>
            <option value="modern">modern</option><option value="legacy">legacy</option>
          </select>
        </div>
      </div>

      <div style={{ marginTop: 18, marginBottom: 8, fontWeight: 600, fontSize: 13, opacity: 0.8 }}>Hello / pickup (defaults baked into cloned avatars)</div>
      <div style={{ display: 'flex', gap: 14 }}>
        <F label="Hello 1 category" k="hello_audio_category_one" w="35%" />
        <F label="Delay 1 (s)" k="say_hello_timing_one" type="number" w="15%" />
        <F label="Hello 2 category" k="hello_audio_category_two" w="35%" />
        <F label="Delay 2 (s)" k="say_hello_timing_two" type="number" w="15%" />
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        <F label="Step response delays (JSON array)" k="script_step_delays" w="50%" />
        <F label="Step iteration limits (JSON array)" k="script_step_iterations_limit" w="50%" />
      </div>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <input type="checkbox" checked={!!form.am_v2_enabled} onChange={e => set('am_v2_enabled', e.target.checked ? 1 : 0)} />
          AM detection v2
        </label>
        <F label="AM v2 threshold" k="am_v2_threshold" type="number" w="160px" />
        <F label="Response delay (ms)" k="response_delay_ms" type="number" w="160px" />
      </div>
      {detail.campaign.source_avatar_id &&
        <div style={{ fontSize: 12, opacity: 0.5, marginBottom: 10 }}>Seeded from avatar {detail.campaign.source_avatar_id}</div>}

      <button className="btn btn-primary" onClick={save} disabled={saving}>
        {saving ? <Loader2 className="spin" size={16} /> : <Save size={16} />} Save settings
      </button>
    </div>
  )
}

// ============================================================ SCRIPTS TAB
function ScriptsTab({ detail, toast, reload }) {
  const cid = detail.campaign.id
  const [editing, setEditing] = useState(null)  // script row or {new:true}
  const empty = { category_name: '', text: '', display_name: '', variation_number: 1 }

  const save = async (row) => {
    try {
      if (row.id) await campaignApi.updateScript(cid, row.id, row)
      else await campaignApi.addScript(cid, row)
      setEditing(null); reload(); toast('Script saved', 'success')
    } catch (e) { toast('Save failed: ' + e.message, 'error') }
  }
  const del = async (row) => {
    if (!window.confirm(`Delete script "${row.category_name}"?`)) return
    try { await campaignApi.deleteScript(cid, row.id); reload(); toast('Deleted', 'success') }
    catch (e) { toast('Delete failed: ' + e.message, 'error') }
  }

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <button className="btn btn-secondary" onClick={() => setEditing({ ...empty })}><Plus size={15} /> Add script</button>
        <span style={{ fontSize: 12, opacity: 0.55, marginLeft: 12 }}>One line per category. Category names must match the audio categories used in the flow.</span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr style={{ textAlign: 'left', opacity: 0.6 }}>
          <th style={{ padding: 8, width: 200 }}>Category</th><th style={{ padding: 8 }}>Script text</th><th style={{ width: 80 }}></th>
        </tr></thead>
        <tbody>
          {detail.scripts.map(s => (
            <tr key={s.id} style={{ borderTop: '1px solid var(--border-color)' }}>
              <td style={{ padding: 8, verticalAlign: 'top', fontWeight: 600 }}>{s.category_name}</td>
              <td style={{ padding: 8, verticalAlign: 'top', opacity: s.text ? 1 : 0.4 }}>{s.text || '(empty — fill in)'}</td>
              <td style={{ padding: 8, whiteSpace: 'nowrap' }}>
                <Edit size={15} style={{ cursor: 'pointer', marginRight: 10, opacity: 0.7 }} onClick={() => setEditing(s)} />
                <Trash2 size={15} style={{ cursor: 'pointer', opacity: 0.7 }} onClick={() => del(s)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <Modal title={editing.id ? 'Edit script' : 'Add script'} onClose={() => setEditing(null)}>
          <Field label="Category name">
            <input className="input" value={editing.category_name} onChange={e => setEditing({ ...editing, category_name: e.target.value })} placeholder="e.g. FEPitch" />
          </Field>
          <Field label="Script text">
            <textarea className="input" rows={5} value={editing.text || ''} onChange={e => setEditing({ ...editing, text: e.target.value })} />
          </Field>
          <Field label="Display name (optional)">
            <input className="input" value={editing.display_name || ''} onChange={e => setEditing({ ...editing, display_name: e.target.value })} />
          </Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
            <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={() => save(editing)}><Check size={15} /> Save</button>
          </div>
        </Modal>
      )}
    </div>
  )
}

// ============================================================ FLOW TAB (avatar structure)
// Clone of the avatar-page Script Steps editor, wired to campaign_actions.
function CampaignActionRow({ action, idx, onEdit, isDraggable }) {
  const t = action.action_type
  const locked = action.can_interrupt === 0 || action.can_interrupt === false
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: action.id, disabled: !isDraggable })
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 1000 : 'auto' }
  return (
    <div ref={setNodeRef} style={style} className={`action-row ${t} ${isDragging ? 'dragging' : ''}`} onClick={() => onEdit(action)}>
      <div className="action-order">
        {isDraggable ? (
          <div className="drag-handle" {...attributes} {...listeners} onClick={(e) => e.stopPropagation()}><GripVertical size={14} /></div>
        ) : (
          <div className="drag-handle disabled"><GripVertical size={14} /></div>
        )}
      </div>
      <div className="action-content">
        {t === 'regular' && (
          <>
            <span className="action-index">{action.order_idx}</span>
            <span className={`condition-badge ${idx === 0 ? 'if' : action.array === '- no condition -' ? 'else' : 'else-if'}`}>
              {idx === 0 ? 'IF' : action.array === '- no condition -' ? 'ELSE' : 'ELIF'}
            </span>
            {action.array !== '- no condition -' && (
              <><span className="condition-name">{getLabelDisplay(action.array)}</span><span className="action-separator">→</span></>
            )}
          </>
        )}
        {t === 'on_silence' && <span className="action-type-label silence">⏱ Silence {action.silence_counter}s</span>}
        {t === 'on_silence' && action.array && action.array !== '- no condition -' && (
          <><span className="condition-name">{getLabelDisplay(action.array)}</span><span className="action-separator">→</span></>
        )}
        {t === 'live' && (
          <>
            <span className="action-type-label" style={{ color: '#f97316', fontWeight: 600 }}>📡 LIVE</span>
            {action.array && action.array !== '- no condition -' && (
              <><span className="condition-name" style={{ color: '#f97316', fontWeight: 600 }}>{getLabelDisplay(action.array)}</span><span className="action-separator">→</span></>
            )}
          </>
        )}
        {(action.audio_category_1 && action.audio_category_1 !== '- none -') ||
         (action.audio_category_2 && action.audio_category_2 !== '- none -') ||
         (action.audio_category_3 && action.audio_category_3 !== '- none -') ? (
          <span className="audio-info">
            <Shuffle size={10} />
            {[action.audio_category_1, action.audio_category_2, action.audio_category_3].filter(a => a && a !== '- none -').join(', ')}
          </span>
        ) : null}
        {action.emit && <span className="emit-info">→ <strong>{action.emit}</strong></span>}
      </div>
      <div className="action-meta">
        <span style={{ opacity: locked ? 1 : 0.3, marginRight: 4 }} title={locked ? 'Non-interruptible' : 'Interruptible'}>
          {locked ? <Lock size={12} /> : <Unlock size={12} />}
        </span>
        <span className={`action-type-tag ${t === 'regular' ? 'regular' : t === 'live' ? 'live' : t === 'on_silence' ? 'silence' : 'not-interested'}`}
          style={t === 'live' ? { background: '#f97316', color: '#fff' } : undefined}>
          {t === 'regular' ? 'REG' : t === 'live' ? 'LIVE' : t === 'on_silence' ? 'SIL' : 'N/I'}
        </span>
        <button className="action-edit-btn" onClick={(e) => { e.stopPropagation(); onEdit(action) }}><Edit size={12} /></button>
      </div>
    </div>
  )
}

function FlowTab({ detail, toast, reload }) {
  const cid = detail.campaign.id
  const [editing, setEditing] = useState(null)
  const [stepTabs, setStepTabs] = useState({})   // per-step sub-tab: global | live
  const [conditionArrays, setConditionArrays] = useState(['- no condition -'])
  const catOptions = [...new Set(detail.scripts.map(s => s.category_name))]
  const audioCatOptions = ['- none -', ...catOptions]

  // keyword categories for the Condition Array dropdown (same source as the avatar page)
  useEffect(() => {
    fetch(`${API_BASE_URL}/keyword-categories?include_keywords=false`)
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setConditionArrays(['- no condition -', ...d.map(c => c.name)]) })
      .catch(() => {})
  }, [])

  // group actions by step, sorted like the avatar page (ELSE default last, rest by order_idx)
  const byStep = {}
  detail.actions.forEach(a => { (byStep[a.step_number] = byStep[a.step_number] || []).push(a) })
  Object.values(byStep).forEach(arr => arr.sort((a, b) => {
    if (a.array === '- no condition -') return 1
    if (b.array === '- no condition -') return -1
    return (a.order_idx || 0) - (b.order_idx || 0)
  }))
  const present = Object.keys(byStep).map(Number)
  const maxStep = present.length ? Math.max(...present) : 1
  const gotos = []
  for (let i = 1; i <= Math.max(1, maxStep); i++) gotos.push(`goto-step-${i}`)
  const emitOptions = ['', ...DISPOSITION_EMITS, ...gotos]
  const silenceEmitOptions = ['', ...gotos, 'back-one-step']
  const stepNums = []
  for (let i = 1; i <= maxStep; i++) stepNums.push(i)

  const save = async (row) => {
    try {
      if (row.id) await campaignApi.updateAction(cid, row.id, row)
      else await campaignApi.addAction(cid, row)
      setEditing(null); reload(); toast('Action saved', 'success')
    } catch (e) { toast('Save failed: ' + e.message, 'error') }
  }
  const del = async (row) => {
    if (!window.confirm('Delete this action?')) return
    try { await campaignApi.deleteAction(cid, row.id); reload(); toast('Deleted', 'success') }
    catch (e) { toast('Delete failed: ' + e.message, 'error') }
  }
  const toggleWhisper = async (sn, on) => {
    const acts = byStep[sn] || []
    try {
      await Promise.all(acts.map(a => campaignApi.updateAction(cid, a.id, { use_whisper: on ? 1 : 0 })))
      reload()
    } catch (e) { toast('Whisper toggle failed: ' + e.message, 'error') }
  }
  const addStep = () => setEditing({ ...blankAction(), step_number: maxStep + 1, order_idx: 0 })

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )
  // Reorder regular (non-default) rows within a step; persist order_idx. Matches the avatar page.
  const handleDragEnd = async (event, stepActions) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const dragged = stepActions.find(a => a.id === active.id)
    const target = stepActions.find(a => a.id === over.id)
    if (!dragged || !target) return
    if (dragged.array === '- no condition -') return
    // everything except the ELSE default can be reordered (incl. on_silence / live)
    const reorderable = stepActions.filter(a => a.array !== '- no condition -')
    const oi = reorderable.findIndex(a => a.id === active.id)
    const ni = reorderable.findIndex(a => a.id === over.id)
    if (oi === -1 || ni === -1) return
    const reordered = arrayMove(reorderable, oi, ni)
    try {
      for (let i = 0; i < reordered.length; i++) {
        await campaignApi.updateAction(cid, reordered[i].id, { order_idx: i + 1 })
      }
      reload()
    } catch (e) { toast('Reorder failed: ' + e.message, 'error') }
  }

  return (
    <div className="script-steps-section">
      <div className="script-steps-header">
        <div className="script-steps-title">
          <Layers size={16} /> Script Steps
          <span className="script-steps-count">{present.length} active</span>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={addStep}><Plus size={12} /> Add step</button>
      </div>

      {stepNums.map((sn) => {
        const acts = byStep[sn] || []
        const subTab = stepTabs[sn] || 'global'
        const globalActs = acts.filter(a => a.action_type !== 'live')
        const liveActs = acts.filter(a => a.action_type === 'live')
        const tabActs = subTab === 'live' ? liveActs : globalActs
        const useWhisper = acts.some(a => a.use_whisper === 1 || a.use_whisper === true)
        const defAudio = (acts.find(a => a.action_type === 'regular' && a.array === '- no condition -') || {}).audio_category_1
        return (
          <div key={sn} className={`script-step ${acts.length > 0 ? 'has-actions' : ''}`}>
            <div className="script-step-header">
              <div className="script-step-title">
                <div className="step-number">{sn}</div>
                <span className="step-name">Script Step {sn}</span>
                {defAudio && defAudio !== '- none -' && acts.length > 0 && (
                  <span className="step-previous-audio" title={defAudio}>"{defAudio}"</span>
                )}
              </div>
              {acts.length > 0 && <div className="step-actions-badge">{acts.length} action{acts.length !== 1 ? 's' : ''}</div>}
              {acts.length > 0 && (
                <div className="step-whisper-toggle" onClick={() => toggleWhisper(sn, !useWhisper)}
                  title={useWhisper ? 'Whisper enabled (click to disable)' : 'Enable Whisper for this step'}>
                  <Zap size={12} className={useWhisper ? 'whisper-active' : ''} />
                  <span className="toggle-label-sm">Whisper</span>
                  <div className={`toggle-switch-sm ${useWhisper ? 'active' : ''}`} />
                </div>
              )}
              <div className="step-header-buttons">
                <button className="btn btn-primary btn-sm" onClick={() => setEditing({ ...blankAction(), step_number: sn, order_idx: globalActs.length, action_type: subTab === 'live' ? 'live' : 'regular' })}>
                  <Plus size={12} />
                </button>
              </div>
            </div>

            {acts.length === 0 ? (
              <p className="step-empty">No actions configured for this step</p>
            ) : (
              <div className="script-step-body">
                <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                  {[['global', 'Global', globalActs.length], ['live', 'Live', liveActs.length]].map(([key, label, count]) => (
                    <button key={key} type="button" onClick={() => setStepTabs(p => ({ ...p, [sn]: key }))}
                      style={{
                        padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        border: subTab === key ? '1px solid #8b5cf6' : '1px solid var(--border-color, #d1d5db)',
                        background: subTab === key ? 'rgba(139,92,246,0.12)' : 'transparent',
                        color: subTab === key ? '#8b5cf6' : 'inherit', display: 'flex', alignItems: 'center', gap: 6
                      }}>
                      {label}
                      <span style={{ fontSize: 10, fontWeight: 700, background: subTab === key ? 'rgba(139,92,246,0.25)' : 'rgba(128,128,128,0.2)', borderRadius: 8, padding: '0 6px' }}>{count}</span>
                    </button>
                  ))}
                </div>
                {tabActs.length === 0 ? (
                  <p className="step-empty">No {subTab} actions for this step</p>
                ) : (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, tabActs)}>
                    <SortableContext items={tabActs.map(a => a.id)} strategy={verticalListSortingStrategy}>
                      <div className="actions-list">
                        {tabActs.map((a, idx) => (
                          <CampaignActionRow key={a.id} action={a} idx={idx} onEdit={setEditing}
                            isDraggable={a.array !== '- no condition -'} />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            )}
          </div>
        )
      })}

      {editing && (
        <ActionModal
          editing={editing} setEditing={setEditing}
          conditionArrays={conditionArrays} audioCatOptions={audioCatOptions}
          emitOptions={emitOptions} silenceEmitOptions={silenceEmitOptions}
          onSave={save} onDelete={del}
        />
      )}
    </div>
  )
}

// Full clone of the avatar-page Edit Action modal, wired to campaign_actions.
function ActionModal({ editing, setEditing, conditionArrays, audioCatOptions, emitOptions, silenceEmitOptions, onSave, onDelete }) {
  const f = editing
  const set = (patch) => setEditing({ ...f, ...patch })
  const at = f.action_type || 'regular'
  const interruptible = !(f.can_interrupt === false || f.can_interrupt === 0)
  const lbl = { fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: 4, display: 'block' }
  return (
    <Modal title={`${f.id ? 'Edit' : 'Add'} Action - Step ${f.step_number}`} onClose={() => setEditing(null)} width={600}>
      {/* Action Type pills */}
      <div className="input-group">
        <label className="input-label">Action Type</label>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {ACTION_TYPE_PILLS.map(p => (
            <button key={p.value} type="button"
              className={`btn btn-sm ${at === p.value ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => set({ action_type: p.value })}>{p.label}</button>
          ))}
        </div>
      </div>

      {/* Condition Array */}
      {(at === 'regular' || at === 'interrupt' || at === 'live') && (
        <div className="input-group">
          <label className="input-label">Condition Array
            {at === 'interrupt' && <span style={{ fontSize: '0.7rem', opacity: 0.65, fontWeight: 400 }}> (required — interrupt rules need a keyword to match against)</span>}
            {at === 'live' && <span style={{ fontSize: '0.7rem', opacity: 0.65, fontWeight: 400 }}> (required — live rules act on the in-progress transcript)</span>}
          </label>
          <SearchableSelect options={conditionArrays} value={f.array || '- no condition -'}
            onChange={(v) => set({ array: v })} placeholder="Select condition..." />
        </div>
      )}

      {/* On Silence: trigger categories + silence seconds */}
      {at === 'on_silence' && (
        <>
          <div className="input-group">
            <label className="input-label">Trigger Audio Categories <span style={{ fontSize: '0.7rem', opacity: 0.65, fontWeight: 400 }}>(rule fires after this exact sequence finishes playing)</span></label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {[1, 2, 3].map(n => (
                <div key={n}>
                  <label style={lbl}>Trigger {n}</label>
                  <SearchableSelect options={audioCatOptions} value={f[`trigger_audio_category_${n}`] || '- none -'}
                    onChange={(v) => set({ [`trigger_audio_category_${n}`]: v })} placeholder="Select trigger..." />
                </div>
              ))}
            </div>
          </div>
          <div className="input-group">
            <label className="input-label">Silence Seconds <span style={{ fontSize: '0.7rem', opacity: 0.65, fontWeight: 400 }}>(seconds to wait before playing the probe)</span></label>
            <input type="number" min="1" className="input" value={f.silence_counter || 0}
              onChange={e => set({ silence_counter: parseInt(e.target.value) || 0 })} />
          </div>
        </>
      )}

      {/* Audio Categories */}
      <div className="input-group">
        <label className="input-label">Audio Categories</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          {[1, 2, 3].map(n => (
            <div key={n}>
              <label style={lbl}>Category {n}</label>
              <SearchableSelect options={audioCatOptions} value={f[`audio_category_${n}`] || '- none -'}
                onChange={(v) => set({ [`audio_category_${n}`]: v })} placeholder="Select category..." />
            </div>
          ))}
        </div>
        <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 8 }}>
          <Shuffle size={10} /> Bot will randomly select one variation from each category.
        </p>
      </div>

      {/* On Silence: after-reply goto */}
      {at === 'on_silence' && (
        <div className="input-group">
          <label className="input-label">After Reply → Go To Step <span style={{ fontSize: '0.7rem', opacity: 0.65, fontWeight: 400 }}>(if the customer replies, evaluate it at this step)</span></label>
          <SearchableSelect options={silenceEmitOptions} value={f.emit || ''}
            onChange={(v) => set({ emit: v })} placeholder="-- Stay on same step --" />
        </div>
      )}

      {/* Interruption Handling */}
      {at !== 'on_silence' && (
        <div className="input-group">
          <label className="input-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Zap size={14} style={{ color: 'var(--warning)' }} /> Interruption Handling
          </label>
          <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', margin: '0 0 8px' }}>
            When user interrupts bot mid-speech, these audios control the conversation flow.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={lbl}>🛑 Interrupt Audio</label>
              <SearchableSelect options={audioCatOptions} value={f.interrupt_audio_category || '- none -'}
                onChange={(v) => set({ interrupt_audio_category: v })} placeholder="e.g., Sorry Go Ahead" />
              <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>Played when user interrupts</span>
            </div>
            <div>
              <label style={lbl}>▶️ Continue Audio</label>
              <SearchableSelect options={audioCatOptions} value={f.continue_audio_category || '- none -'}
                onChange={(v) => set({ continue_audio_category: v })} placeholder="e.g., As I Was Saying" />
              <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>Played before resuming (if no keyword match)</span>
            </div>
          </div>
        </div>
      )}

      {/* Interruptible box */}
      {at !== 'on_silence' && (
        <div className="input-group">
          <label style={{
            display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: 12, borderRadius: 8,
            background: interruptible ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${interruptible ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`
          }}>
            <input type="checkbox" checked={interruptible} onChange={e => set({ can_interrupt: e.target.checked ? 1 : 0 })} style={{ width: 18, height: 18 }} />
            <div>
              <span style={{ fontWeight: 500 }}>{interruptible ? '🔓 Interruptible' : '🔒 Non-Interruptible'}</span>
              <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                {interruptible ? 'Customer can interrupt this audio by speaking' : 'Customer speech will NOT interrupt this audio (e.g., hello, transfer)'}
              </p>
            </div>
          </label>
        </div>
      )}

      {/* Whisper */}
      {at !== 'on_silence' && (
        <div className="input-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={f.use_whisper === 1 || f.use_whisper === true} onChange={e => set({ use_whisper: e.target.checked ? 1 : 0 })} />
            <Zap size={14} /> Use Whisper for this action
          </label>
        </div>
      )}

      {/* Emit Event */}
      {at !== 'on_silence' && (
        <div className="input-group">
          <label className="input-label">Emit Event</label>
          <SearchableSelect options={emitOptions} value={f.emit || ''}
            onChange={(v) => set({ emit: v })} placeholder="-- No event (advance) --" />
        </div>
      )}

      {/* Step / Order */}
      <div style={{ display: 'flex', gap: 12 }}>
        <Field label="Step" w="100px"><input className="input" type="number" value={f.step_number ?? ''} onChange={e => set({ step_number: e.target.value })} /></Field>
        <Field label="Order" w="100px"><input className="input" type="number" value={f.order_idx ?? ''} onChange={e => set({ order_idx: e.target.value })} /></Field>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 8 }}>
        <div>{f.id && <button className="btn btn-secondary" onClick={() => onDelete(f)} style={{ color: '#ef4444' }}><Trash2 size={14} /> Delete</button>}</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onSave(f)}><Check size={15} /> Save</button>
        </div>
      </div>
    </Modal>
  )
}

// ============================================================ CREATE MODAL
function CreateModal({ onClose, onCreated, toast, audit }) {
  const [mode, setMode] = useState('seed')  // 'seed' | 'blank'
  const [avatars, setAvatars] = useState([])
  const [avatarId, setAvatarId] = useState('')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    avatarApi.getAll().then(({ data }) => {
      const list = Array.isArray(data) ? data : (data.data || data.avatars || [])
      setAvatars(list.filter(a => !a.is_archived && !a.archived))
    }).catch(() => {})
  }, [])

  const create = async () => {
    setBusy(true)
    try {
      if (mode === 'seed') {
        if (!avatarId) { toast('Pick an avatar to seed from', 'warning'); setBusy(false); return }
        const { data } = await campaignApi.seedFromAvatar(avatarId, { name: name.trim() || undefined, ...audit() })
        onCreated(data.campaign)
      } else {
        if (!name.trim()) { toast('Enter a campaign name', 'warning'); setBusy(false); return }
        const { data } = await campaignApi.create({ name: name.trim(), ...audit() })
        onCreated(data.campaign)
      }
    } catch (e) { toast('Create failed: ' + e.message, 'error'); setBusy(false) }
  }

  return (
    <Modal title="New Campaign" onClose={onClose}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[['seed', 'From an avatar', Copy], ['blank', 'Blank', Plus]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setMode(k)} className={'btn ' + (mode === k ? 'btn-primary' : 'btn-secondary')}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {mode === 'seed' ? (
        <>
          <Field label="Seed from avatar (copies flow + pulls script text from its voice set)">
            <select className="input" value={avatarId} onChange={e => setAvatarId(e.target.value)}>
              <option value="">— select avatar —</option>
              {avatars.map(a => <option key={a.id} value={a.id}>{a.id} · {a.name}{a.company ? ' · ' + a.company : ''}</option>)}
            </select>
          </Field>
          <Field label="Campaign name (optional — defaults to avatar name)">
            <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. FE" />
          </Field>
        </>
      ) : (
        <Field label="Campaign name">
          <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. FE" />
        </Field>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={create} disabled={busy}>
          {busy ? <Loader2 className="spin" size={15} /> : <Check size={15} />} Create
        </button>
      </div>
    </Modal>
  )
}

// ============================================================ shared bits
function Field({ label, children, w }) {
  return (
    <div style={{ marginBottom: 12, width: w || '100%' }}>
      <label style={{ display: 'block', fontSize: 12, opacity: 0.6, marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  )
}

function Modal({ title, children, onClose, width = 560 }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: width }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: '76vh', overflowY: 'auto' }}>
          {children}
        </div>
      </div>
    </div>
  )
}
